import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from 'react-router-dom';

import { BadgeInfo, ArrowBigRight } from "lucide-react";
import he from 'he';
import styles from "./QuestionsInde.module.scss";
import { DEBUG, PATH } from '../../../utils/constants';
import useSafeAsyncEffect from '../../../utils/hooks/useSafeAsyncEffect';
import MarkdownRenderer from '../../../utils/MarkdownRenderer/MarkdownRenderer';
import SmartRenderer from '../../../utils/SmartRenderer/SmartRenderer';

import { sendGameScore } from '../../../utils/api/scoresAPI';

import { LanguageContext } from '../../../context/LanguageContext';
import translations from '../../../utils/translations/QuestionIndeTranslations';


const path = PATH;
const debug = DEBUG;


const QuestionsInde = ({ game, questions, repLibres, repQcm, onFinishGame, setQuestionAchieved, startQuestionTimer, saveQuestionsToStorage, updateQuestionField }) => {

  // Utilisation du contexte de langue global et accès simplifié aux traductions
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.fr;

  const navigate = useNavigate();

// --------------------- STATES --------------------------


  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const [userAnswers, setUserAnswers] = useState({});
  const [feedback, setFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [showHelperModal, setShowHelperModal] = useState(false);

  // Spécifique aux jeux indépendants
  const [isHintModalOpen, setIsHintModalOpen] = useState(false);
  const [isHintVisible, setIsHintVisible] = useState(false); // Pour l'effet de fondu
  const [currentHint, setCurrentHint] = useState("");
  const [currentMessage, setCurrentMessage] = useState("");

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;


// --------------------- EFFECTS --------------------------


  useEffect(() => {
    if (currentQuestion && !currentQuestion.startTime) {
      startQuestionTimer(currentQuestion.id);
      saveQuestionsToStorage();
      if (debug) console.log("⏱️ Timer initialisé pour la question ID", currentQuestion.id);
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (!questions.length) return;
  
    const nextIndex = findNextUnachievedIndex(currentQuestionIndex, questions);
  
    if (nextIndex === -1) {
      if (debug) console.log("🎯 Toutes les questions sont terminées !");
      onFinishGame();
    } else if (nextIndex !== currentQuestionIndex) {
      if (debug) console.log(`➡️ Passage automatique de la question ${currentQuestionIndex} à ${nextIndex}`);
      setCurrentQuestionIndex(nextIndex);
    }
  }, [currentQuestionIndex, questions]);
    
  
  // Pour afficher le temps écoulé sur une question 
  useSafeAsyncEffect(async (isMounted) => {
    const interval = setInterval(() => {
      if (isMounted.current && currentQuestion?.startTime) {
        const now = new Date();
        const start = new Date(currentQuestion.startTime);
        const diff = Math.floor((now - start) / 1000);
  
        const days = Math.floor(diff / (60 * 60 * 24));
        const hours = Math.floor((diff % (60 * 60 * 24)) / (60 * 60));
        const minutes = Math.floor((diff % (60 * 60)) / 60);
        const seconds = diff % 60;
  
        setElapsedTime({ days, hours, minutes, seconds });
      }
    }, 1000);
  
    return () => clearInterval(interval); // Nettoie le timer
  }, [currentQuestion?.startTime], { debug: debug });
  
  
  const findNextUnachievedIndex = (startIndex, questions) => {
    for (let i = startIndex; i < questions.length; i++) {
      if (!questions[i]?.achieved) {
        return i;
      }
    }
    return -1; // Plus rien à jouer
  };
  
// --------------------- EVENTS --------------------------

  const handleAnswerChange = (event) => {
    setUserAnswers({
      ...userAnswers,
      [currentQuestion.id]: event.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateAnswer();
  };

  // S'appuie sur le système de boussole non explicité dans les jeux mais utile pour le calcul du score !
  const handleFail = () => {
    setIsModalOpen(false);
    setFeedback(false);
    setFeedbackText("");

    if (currentQuestion.nbCompass === 0) {
      return; 
    }

    const newCompassCount = Math.max(currentQuestion.nbCompass - 1, 0);
    updateQuestionField(currentQuestion.id, 'nbCompass', newCompassCount);
    saveQuestionsToStorage();
  };

//------------------------- FUNCTIONS -----------------------------

  // function normalize(str) {
  //   return str
  //     .normalize("NFD") // Supprime les accents
  //     .replace(/[\u0300-\u036f]/g, "")
  //     .toLowerCase()
  //     .trim()
  //     .replace(/\s+/g, " "); // supprime les espaces multiples
  // }

  function normalize(str) {
    return he.decode(str || "")             // 🔥 Décodage des entités HTML
      .normalize("NFD")                     // Supprime les accents
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");                // supprime les espaces multiples
  }

  const validateAnswer = async () => {
    const rawUserAnswer = userAnswers[currentQuestion.id] || "";
    const userAnswer = normalize(rawUserAnswer);

    let isCorrect = false;

    if (currentQuestion.type === "libre") {

      const rawAnswers = repLibres.find(
        (rep) => rep.idQuestion === currentQuestion.id
      )?.possibilities;

      let validAnswers = [];
      try {
        validAnswers = typeof rawAnswers === "string" ? JSON.parse(rawAnswers) : rawAnswers;
      } catch (e) {
        console.error("Échec du parse JSON des réponses libres :", e);
      }
      isCorrect = validAnswers?.some(ans => normalize(ans) === userAnswer);

    } else if (currentQuestion.type === "qcm") {

      const selectedAnswer = parseInt(userAnswer);
      isCorrect = repQcm.some((reponse) => parseInt(reponse.id) === selectedAnswer && parseInt(reponse.isTrue) === 1);
    }

    if(isCorrect) {
      const now = new Date();
      const start = new Date(currentQuestion.startTime);
      const diff = Math.floor((now - start) / 1000); // Calcul du temps passé sur la question

      await setQuestionAchieved(currentQuestion.id, {
        duration: diff,
        nbCompass: currentQuestion.nbCompass,
        aborted: false,
      });
    }

    setFeedback(isCorrect);
    setFeedbackText(isCorrect ? t.correctAnswer : t.tryAgain);
    setIsModalOpen(true);
  };

  const nextQuestion = async () => {

    // Spécifique aux jeux indépendants, permet d'afficher la modale d'indice / message avec un délai :
    if (currentQuestion.cle_indice || currentQuestion.message) {
      if (currentQuestion.message) setCurrentMessage(currentQuestion.message);
      if (currentQuestion.cle_indice) setCurrentHint(currentQuestion.cle_indice);
      setTimeout(() => {
        setIsHintModalOpen(true);
        setTimeout(() => setIsHintVisible(true), 50); // Délai avant fondu
      }, 500);
    }

    setIsModalOpen(false); // Ferme la modale de validation de la réponse
    setFeedback(false); // Booléen - Gère la condition correcte / incorrecte, affichage de la bonne image etc...
    setFeedbackText(""); // String - Texte selon si réponse bonne ou mauvaise

    await saveCurrentScoreToLocal();

    if (isLastQuestion) {
      onFinishGame(); 
      return;
    }

    // Remise à zéro de l'affichage local
    setElapsedTime({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  };


  const handleFinishGame = async () => {
    await saveCurrentScoreToLocal();
    onFinishGame();
  };


  const saveCurrentScoreToLocal = async () => {
    const now = new Date(); // Réinitialise la date et l'heure du début de la question (suivante)
    if (currentQuestion?.startTime) { // on vérifie si la question actuelle a bien un timer qui a été lancé
      const start = new Date(currentQuestion.startTime); // on crée un objet Date à partir de ce timer
      const diff = Math.floor((now - start) / 1000); // On calcule le temps en secondes écoulé depuis le début de la question
      // await setQuestionAchieved(currentQuestion.id, { duration: diff, nbCompass: currentQuestion.nbCompass, aborted: false }); // On met à jour la question que l'on vient de terminer dans le tableau local
      await scoreManagement(localStorage.getItem("userId"), currentQuestion.id, diff, currentQuestion.nbCompass, false, localStorage.getItem(`progression_inde_${game.id}`)); // On enregistre le score via l'api

      // Sauvegarder l'état final (initialement que dans handleFinishGame, à voir si supporté correctement !)
      saveQuestionsToStorage();
    }
  };


  async function scoreManagement(idUser, idQuestion, time, compass, aborted, idProgression) {
    const score = { idUser, idQuestion, time, compass, aborted, idProgression };

    const success = await sendGameScore(score);
    if (success && !success.message) {
        console.log("Score envoyé !");
    } else {
        console.log("Score sauvegardé localement, en attente de connexion...");
    }
  }

  // Spécifique aux jeux indépendants :
  const closeHintModal = () => {
  
      setIsHintVisible(false);
      setTimeout(() => setIsHintModalOpen(false), 500); // Attendre la fin du fondu avant de fermer
      setCurrentHint("");
      setCurrentMessage("");
      
  };
//------------------------- RENDERINGS -----------------------------

  // Déclenché lorsqu'il n'y a pas toutes les réponses aux questions ou qu'une erreur est survenue lors de leur récupération...
  if (!currentQuestion) return (
    <>
      <p className={styles.loadingQuestions}>{t.loadingQuestions}</p>
      <button className={styles.returnButton} onClick={() => navigate("/")} >
          {t.backToHome}
      </button>
    </>
    
  );

  return (
    <div className={styles.container}>
      <p className={styles.questionRank}>
        {t.questionLabel} {currentQuestionIndex + 1}/{questions.length}
      </p>

      {/* <p>
        {elapsedTime.days > 0 && ` ${elapsedTime.days} jour${elapsedTime.days > 1 ? 's' : ''},`}
        {elapsedTime.hours > 0 && ` ${elapsedTime.hours}:`}
        {elapsedTime.minutes > 0 && ` ${elapsedTime.minutes}:`}
        {elapsedTime.seconds}"{elapsedTime.seconds > 1 ? '' : ''}
      </p> */}
      <div className={styles.compassContainer}>
          {Array.from({ length: currentQuestion.nbCompass }).map((_, i) => (
              <span key={i}>🧭</span>
          ))}
      </div>

      <div className={styles.questionText}>
        {/* <MarkdownRenderer content={currentQuestion.libelle} /> */}
        <div className="rt-content">
          <SmartRenderer content={currentQuestion.libelle} />
        </div>
      </div>
      {currentQuestion.img && (
        <img src={`${path}${currentQuestion.img}`} alt="Illustration" className={styles.questionImage} />
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles.answerContainer}>
          {currentQuestion.type === "libre" ? (
            <input
              type="text"
              placeholder={t.yourAnswer}
              className={styles.answerInput}
              value={userAnswers[currentQuestion.id] || ""}
              onChange={handleAnswerChange}
            />
          ) : (
            <div className={styles.qcmOptions}>
              {repQcm
                .filter((rep) => rep.idQuestion === currentQuestion.id)
                .map((option) => (
                  <label key={option.id} className={styles.option}>
                    <input
                      type="radio"
                      name={`qcm-${currentQuestion.id}`}
                      value={option.id}
                      onChange={handleAnswerChange}
                      className={styles.hiddenRadio}
                    />
                    <div className={styles.optionCard}>
                      {option.img ? (
                        <img src={`${path}${option.img}`} alt="Option" className={styles.optionImage} />
                      ) : (
                        <span className={styles.optionText}><MarkdownRenderer content={option.txt} /></span>
                      )}
                    </div>
                  </label>
                ))}
            </div>
          )}
        </div>

        <div className={styles.bottomContainer}>
          <div className={styles.buttonsContainer}>
            <button onClick={validateAnswer} className={styles.validateButton}>
              {t.validateAnswer} <ArrowBigRight size={30} />
            </button>
            <div className={styles.iconContainer}>
              <BadgeInfo 
                className={currentQuestion.helper || currentQuestion.img_helper
                  ? styles.activeHint
                  : styles.icon} 
                size={36} 
                onClick={() => {
                  if (currentQuestion.helper || currentQuestion.img_helper) {
                    setShowHelperModal(true);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </form>

      {/* Modale pour la validation des réponses */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <p className={styles.modalText}>{feedbackText}</p>
            {isLastQuestion && feedback ? (
                <>
                    <div><img src="/assets/img/sirene1opt.gif" alt="Illustration" className={styles.image} /></div>
                    <button onClick={handleFinishGame} className={styles.nextButton}>
                        {t.finishGame}
                    </button>
                </>
            ) : feedback ? (
                <>
                    <div><img src="/assets/img/sirene1opt.gif" alt="Illustration" className={styles.image} /></div>
                    <button onClick={nextQuestion} className={styles.nextButton}>
                        {t.nextQuestion}
                    </button>
                </>
            ) : (
                <>
                    <div><img src="/assets/img/sirene2opt.gif" alt="Illustration" className={styles.image} /></div>
                    <button onClick={handleFail} className={styles.retryButton}>
                        {t.retry}
                    </button>
                </>
            )}
          </div>
        </div>
      )}

      {/* Modale pour les indices pour reconstituer le mot de passe */}
      {isHintModalOpen && (
        <div className={`${styles.modalOverlay} ${isHintVisible ? styles.fadeIn : styles.fadeOut}`}>
          <div className={styles.modal}>
            {/* Bouton de fermeture */}
            <button className={styles.closeButton} onClick={closeHintModal}>×</button>

            <div className={styles.modalText}>
              {currentMessage ? <MarkdownRenderer content={currentMessage} /> : ""}
            </div>
            
            {/* <p className={styles.modalText1}> {t.hintFoundMessage} </p>
            <p className={styles.indiceKey}>{currentHint}</p> */}
            {currentHint !== "" && (
              <>
                <p className={styles.indiceKey}>{currentHint}</p>
                <p className={styles.modalText1}>{t.hintFoundMessage}</p>
              </>
            )}
      
          </div>
        </div>
      )}


      {/* Modale pour les indices de progression */}
      {showHelperModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            {currentQuestion.helper && (
              <div className={styles.modalText}><MarkdownRenderer content={currentQuestion.helper} /></div>
            )}
            {currentQuestion.img_helper && (
              <img
                src={`${path}${currentQuestion.img_helper}`}
                alt="Indice visuel"
                className={styles.helperImage}
              />
            )}
            {!currentQuestion.helper && !currentQuestion.img_helper && (
              <p className={styles.modalText}>{t.noHintAvailable}</p>
            )}
            <button onClick={() => setShowHelperModal(false) } className={styles.nextButton}>
              {t.closeButton}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default QuestionsInde;
